import { ExportSingleConversationButton } from '@/components/conversations/conversation-chat/ExportSingleConversationButton';
import { ManualReplyModeMenu } from '@/components/conversations/conversation-chat/ManualReplyModeMenu';
import { ManageClassificationsModal } from '@/components/conversations/ManageClassificationsModal';
import { ToggleAIConversationDialog } from '@/components/conversations/conversation-chat/ToggleAIConversationDialog';
import { ProfileAvatar } from '@/components/shared/ProfileAvatar';
import { Select, Switch } from '@/components/ui';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/AlertDialog';
import { useCustomClassifications } from '@/hooks/useCustomClassifications';
import { useI18n } from '@/context/I18nProvider';
import { cn, formatPhoneNumber } from '@/lib/utils';
import { getClassificationStyle } from '@/lib/classification-utils';
import { ApiClient } from '@/services/api-client';
import { IChannel } from '@/services/types';
import { Classification, ConversationPreview } from '@/services/types/Lead.types';
import { GetConversationsResponse, GetLeadByIDResponse, SearchIndexResponse } from '@/services/types/global.types';
import { AIStar, ChevronLeft, Globe, Loader2, Sequence, Settings, Trash2, User } from '@/components/ui/icons/Icons';
import { useOrganization } from '@clerk/nextjs';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
type ConversationHeaderProps = {
  id: string;
  name: string;
  channel: IChannel;
  contactIdentifier: string;
  isActive: boolean;
  /** Conversation override of the workspace manual-reply setting; null follows it. */
  keepAgentOnManualReply?: boolean | null;
  classification: string;
  profilePictureURL?: string;
  businessWhatsappPhoneNumber?: string;
  businessInstagramUsername?: string;
  assignedToUserId?: string;
  agentName?: string;
  /** Sequence that enrolled the lead; its follow-ups and agent drive this conversation. */
  sequenceId?: string;
  /** Undefined when the sequence has been deleted. */
  sequenceName?: string;
  /** False when the sequence is paused. */
  sequenceActive?: boolean;
  deselectConversation: () => void;
  isMobile?: boolean;
  /** Translation of the conversation into the user's language (manual). */
  translation?: {
    enabled: boolean;
    isTranslating: boolean;
    /** Display name of the lead's language, once known. */
    leadLanguageName?: string;
    /** The lead writes in the user's language: nothing to translate. */
    sameLanguage: boolean;
    onToggle: () => void;
  };
};
const OrgMembersParams = {
  memberships: {
    pageSize: 100,
    keepPreviousData: true
  }
};
const UNASSIGNED_VALUE = '__unassigned__';
const ConversationHeader = ({
  id,
  name,
  channel,
  contactIdentifier,
  isActive,
  keepAgentOnManualReply,
  classification,
  profilePictureURL = '',
  businessWhatsappPhoneNumber,
  businessInstagramUsername,
  assignedToUserId,
  agentName,
  sequenceId,
  sequenceName,
  sequenceActive,
  deselectConversation,
  isMobile,
  translation
}: ConversationHeaderProps) => {
  const {
    t
  } = useI18n();
  const queryClient = useQueryClient();
  const contactedFrom = formatPhoneNumber(businessWhatsappPhoneNumber) || businessInstagramUsername;
  const mDeleteLead = useMutation({
    mutationFn: () => ApiClient.lead.deleteLead({
      leadId: id
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getConversations']
      });
      queryClient.invalidateQueries({
        queryKey: ['lead.getSearchIndex']
      });
      queryClient.removeQueries({
        queryKey: ['lead.getLead', {
          id
        }]
      });
      deselectConversation();
      toast.success(t('conversations.leadDeleted'));
    },
    onError: () => {
      toast.error(t('conversations.leadDeleteError'));
    }
  });
  const deleteButton = <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="flex items-center justify-center h-7 px-2.5 py-2 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 transition-colors shadow-none" disabled={mDeleteLead.isPending}>
          <Trash2 className="size-4 text-red-500" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-md p-6">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('conversations.deleteLeadTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('conversations.deleteLeadConfirm')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90" onClick={e => {
          e.preventDefault();
          mDeleteLead.mutate();
        }} disabled={mDeleteLead.isPending}>
            {t('common.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>;
  const agentSwitch = <div className="flex h-4 w-[26px] items-center">
      <ToggleAIConversationDialog conversationId={id} isAIEnabled={isActive} title={isActive ? t('conversations.disableAI') : t('conversations.enableAI')} description={isActive ? t('conversations.disableAIConfirm') : t('conversations.enableAIConfirm')} trigger={<Switch height={12} checkedColor="#1F2937" uncheckedColor="#F97316" checked={isActive} disabled={false} />} shouldOpenDialog={true} />
    </div>;
  const manualReplyMenu = <ManualReplyModeMenu leadId={id} keepAgentOnManualReply={keepAgentOnManualReply} />;
  const {
    data: customClassifications = []
  } = useCustomClassifications();
  const [manageOpen, setManageOpen] = useState(false);
  const {
    memberships
  } = useOrganization(OrgMembersParams);
  const memberOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
    }[] = [{
      value: UNASSIGNED_VALUE,
      label: agentName || t('conversations.unassigned')
    }];
    if (memberships?.data) {
      for (const m of memberships.data) {
        const first = m.publicUserData?.firstName ?? '';
        const last = m.publicUserData?.lastName ?? '';
        const memberName = [first, last].filter(Boolean).join(' ') || m.publicUserData?.identifier || '';
        if (m.publicUserData?.userId) {
          options.push({
            value: m.publicUserData.userId,
            label: memberName
          });
        }
      }
    }
    return options;
  }, [agentName, memberships?.data, t]);
  const mAssignLead = useMutation({
    mutationFn: (userId: string | null) => ApiClient.lead.assignLead({
      leadId: id,
      userId
    }),
    onSuccess: (_data, userId_0) => {
      queryClient.setQueryData<GetLeadByIDResponse>(['lead.getLead', {
        id
      }], old => {
        if (!old?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            LeadInformation: {
              ...old.data.LeadInformation,
              AssignedToUserId: userId_0 ?? undefined
            }
          }
        };
      });

      // Update all conversation caches
      queryClient.setQueriesData<InfiniteData<GetConversationsResponse>>({
        queryKey: ['lead.getConversations']
      }, old_0 => {
        if (!old_0?.pages) return old_0;
        return {
          ...old_0,
          pages: old_0.pages.map(page => ({
            ...page,
            data: {
              ...page.data,
              Previews: (page.data?.Previews || []).map(p => p.LeadID === id ? {
                ...p,
                AssignedToUserId: userId_0 ?? undefined
              } : p)
            }
          }))
        };
      });

      // Update search index caches
      queryClient.setQueriesData<SearchIndexResponse>({
        queryKey: ['lead.getSearchIndex']
      }, old_1 => {
        if (!old_1?.data?.Previews) return old_1;
        return {
          ...old_1,
          data: {
            ...old_1.data,
            Previews: old_1.data.Previews.map(p_0 => p_0.LeadID === id ? {
              ...p_0,
              AssignedToUserId: userId_0 ?? undefined
            } : p_0)
          }
        };
      });
      toast.success(t('conversations.assignmentUpdated'));
    },
    onError: () => {
      toast.error(t('conversations.assignmentUpdateError'));
    }
  });
  const assignmentSelect = <Select value={assignedToUserId || UNASSIGNED_VALUE} onValueChange={value => mAssignLead.mutate(value === UNASSIGNED_VALUE ? null : value)} disabled={mAssignLead.isPending}>
      <Select.Trigger className={cn('h-7 gap-1 px-2.5 py-1 rounded-lg border border-gray-100 shadow-none text-xs font-medium shrink-0', isMobile ? 'w-[92px]' : 'w-[110px] @3xl:w-[140px]')}>
        <span className="truncate">
          <Select.Value />
        </span>
      </Select.Trigger>
      <Select.Content className="rounded-lg border-gray-100 min-w-[200px]">
        {memberOptions.map(option => <Select.Item key={option.value} value={option.value} className="text-sm rounded-lg">
            <span className="flex items-center gap-1.5 min-w-0">
              {option.value === UNASSIGNED_VALUE ? <AIStar className="size-3.5 shrink-0" /> : <User className="size-3.5 shrink-0" />}
              <span className="truncate">{option.label}</span>
            </span>
          </Select.Item>)}
      </Select.Content>
    </Select>;
  const classificationOptions = [{
    value: Classification.POSITIVE,
    label: t('conversations.positive'),
    style: getClassificationStyle(Classification.POSITIVE, [])
  }, {
    value: Classification.ENGAGED,
    label: t('conversations.engaged'),
    style: getClassificationStyle(Classification.ENGAGED, [])
  }, {
    value: Classification.UNDETERMINED,
    label: t('conversations.noAnswer'),
    style: getClassificationStyle(Classification.UNDETERMINED, [])
  }, {
    value: Classification.NEGATIVE,
    label: t('conversations.negative'),
    style: getClassificationStyle(Classification.NEGATIVE, [])
  }, ...customClassifications.map(cc => ({
    value: cc.id,
    label: cc.label,
    style: {
      color: cc.text_color,
      bg: cc.color
    }
  }))];
  const mUpdateClassification = useMutation({
    mutationFn: (newClassification: string) => ApiClient.lead.updateConversationClassification({
      leadId: id,
      classification: newClassification
    }),
    onSuccess: (_data_0, newClassification_0) => {
      // Update the individual lead cache so the header dropdown and list filter see the new classification
      queryClient.setQueryData<GetLeadByIDResponse>(['lead.getLead', {
        id
      }], old_2 => {
        if (!old_2?.data) return old_2;
        return {
          ...old_2,
          data: {
            ...old_2.data,
            Classification: newClassification_0
          }
        };
      });

      // Find the preview from any existing cache so we can add it to caches that don't have it
      let updatedPreview: ConversationPreview | null = null;
      const allQueries = queryClient.getQueriesData<InfiniteData<GetConversationsResponse>>({
        queryKey: ['lead.getConversations']
      });
      for (const [, data] of allQueries) {
        if (updatedPreview) break;
        if (!data?.pages) continue;
        for (const page_0 of data.pages) {
          const found = page_0?.data?.Previews?.find(p_1 => p_1.LeadID === id);
          if (found) {
            updatedPreview = {
              ...found,
              Classification: newClassification_0
            };
            break;
          }
        }
      }

      // Update Classification in all cached previews AND add to caches that don't have it yet
      // (the page-level client-side filter will handle showing only matching leads per tab)
      queryClient.setQueriesData<InfiniteData<GetConversationsResponse>>({
        queryKey: ['lead.getConversations']
      }, old_3 => {
        if (!old_3?.pages) return old_3;
        const hasLead = old_3.pages.some(page_1 => page_1?.data?.Previews?.some(p_2 => p_2.LeadID === id));
        return {
          ...old_3,
          pages: old_3.pages.map((page_2, pageIdx) => ({
            ...page_2,
            data: {
              ...page_2.data,
              Previews: (() => {
                const previews = (page_2.data?.Previews || []).map(p_3 => p_3.LeadID === id ? {
                  ...p_3,
                  Classification: newClassification_0
                } : p_3);
                // If this cache doesn't have the lead, add it to the first page
                if (!hasLead && pageIdx === 0 && updatedPreview) {
                  return [updatedPreview, ...previews];
                }
                return previews;
              })()
            }
          }))
        };
      });

      // Refetch active conversation queries immediately so the list updates
      queryClient.invalidateQueries({
        queryKey: ['lead.getConversations']
      });
      queryClient.invalidateQueries({
        queryKey: ['lead.getSearchIndex']
      });
      toast.success(t('conversations.classificationUpdated'));
    },
    onError: () => {
      toast.error(t('conversations.classificationUpdateError'));
    }
  });
  const currentOption = classificationOptions.find(o => o.value === classification);
  const classificationSelect = <Select value={classification} onValueChange={value_0 => mUpdateClassification.mutate(value_0)} disabled={mUpdateClassification.isPending}>
      <Select.Trigger className={cn('h-7 gap-1.5 px-3 py-1 rounded-lg border border-gray-100 shadow-none text-xs font-medium shrink-0', isMobile ? 'w-[92px]' : 'w-[110px] @3xl:w-[140px]')} style={currentOption ? {
      color: currentOption.style.color,
      backgroundColor: currentOption.style.bg
    } : undefined}>
        <Select.Value />
      </Select.Trigger>
      <Select.Content className="rounded-lg border-gray-100" footer={<button className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors border-t border-gray-100" onPointerDown={e_0 => e_0.stopPropagation()} onClick={() => setManageOpen(true)}>
            <Settings className="size-3" />
            {t('customClassifications.title')}
          </button>}>
        {classificationOptions.map(option_0 => <Select.Item key={option_0.value} value={option_0.value} className="text-sm rounded-lg">
            {option_0.label}
          </Select.Item>)}
      </Select.Content>
    </Select>;
  const manageModal = <ManageClassificationsModal open={manageOpen} onOpenChange={setManageOpen} />;

  // Jump to the sequence that created this lead: when follow-ups don't go out,
  // a paused, deleted or misconfigured sequence is the first thing to check.
  const sequenceState = !sequenceId ? null : !sequenceName ? 'deleted' : sequenceActive === false ? 'paused' : 'active';
  const sequenceButton = sequenceState ? <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
        // The editor 404s on a deleted sequence: fall back to the list.
        href={sequenceState === 'deleted' ? '/sequences' : `/sequences/${sequenceId}`} className={cn('inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium shadow-none transition-colors', sequenceState === 'active' && 'border-gray-100 bg-white text-text-primary hover:border-blue-300 hover:text-blue-500', sequenceState === 'paused' && 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100', sequenceState === 'deleted' && 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100')}>
            <Sequence className="size-4 shrink-0" />
            {isMobile ? null : <span className="max-w-[150px] truncate">
                {sequenceState === 'active' ? t('conversations.sequence') : sequenceState === 'paused' ? t('conversations.sequencePaused') : t('conversations.sequenceDeleted')}
              </span>}
          </Link>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {sequenceState === 'active' ? t('conversations.openSequence', {
          name: sequenceName ?? ''
        }) : sequenceState === 'paused' ? t('conversations.sequencePausedHint', {
          name: sequenceName ?? ''
        }) : t('conversations.sequenceDeletedHint')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider> : null;
  const translationButton = translation && !translation.sameLanguage ? <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={translation.onToggle} aria-pressed={translation.enabled} className={cn('inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium shadow-none transition-colors', translation.enabled ? 'border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100' : 'border-gray-100 bg-white text-text-primary hover:border-blue-300 hover:text-blue-500')}>
              {translation.isTranslating ? <Loader2 className="size-4 shrink-0 animate-spin" /> : <Globe className="size-4 shrink-0" />}
              {isMobile ? null : <span className="hidden max-w-[140px] truncate @3xl:inline">
                  {translation.enabled ? t('conversations.translation.on') : translation.leadLanguageName || t('conversations.translation.translate')}
                </span>}
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {translation.enabled ? t('conversations.translation.turnOff') : translation.leadLanguageName ? t('conversations.translation.turnOnFrom', {
          language: translation.leadLanguageName
        }) : t('conversations.translation.turnOn')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider> : null;

  // ------------------ RENDER ---------------------------------------

  return <header className={cn('@container flex items-center justify-between border-border border-b', isMobile ? 'gap-3 px-3 py-2.5' : 'gap-5 pl-4 pr-6 py-3')}>
      <div className={cn('flex items-center min-w-0 flex-1', isMobile ? 'gap-2' : 'gap-4')}>
        {isMobile ? <ChevronLeft className="size-5 shrink-0 text-muted-foreground" onClick={deselectConversation} /> : null}

        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ProfileAvatar profilePictureURL={profilePictureURL} displayName={name} leadID={id} channel={channel} className="size-7 shrink-0" size="lg" />
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="text-sm font-medium leading-5 text-text-primary truncate" title={name}>
              {name}
            </span>
            <div className="flex items-center gap-2 min-w-0">
              {channel === 'INSTAGRAM' && contactIdentifier ? <a href={`https://www.instagram.com/${contactIdentifier.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" title={contactIdentifier} className="text-xs font-medium leading-4 text-muted-foreground hover:text-foreground hover:underline transition-colors truncate shrink-0 max-w-full">
                  {contactIdentifier}
                </a> : <span title={contactIdentifier} className="text-xs font-medium leading-4 text-muted-foreground truncate shrink-0 max-w-full select-all">
                  {contactIdentifier}
                </span>}
              {contactedFrom ? <div className="hidden @3xl:flex items-center gap-2 min-w-0">
                  <div className="h-[3px] w-[3px] shrink-0 rounded-full bg-[#D9D9D9]" />
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="text-xs leading-4 text-muted-foreground shrink-0">
                      {t('conversations.from')}:
                    </span>
                    <span title={contactedFrom} className="text-xs font-medium leading-4 text-muted-foreground truncate">
                      {contactedFrom}
                    </span>
                  </div>
                </div> : null}
            </div>
          </div>
        </div>
      </div>

      {isMobile ? <div className="flex shrink-0 items-center justify-end gap-1.5">
          {translationButton}
          {sequenceButton}
          {assignmentSelect}
          {classificationSelect}
          {agentSwitch}
          {manualReplyMenu}
        </div> : <div className="flex items-center gap-2 shrink-0">
          {translationButton}
          {sequenceButton}
          {assignmentSelect}
          {classificationSelect}
          <span className="hidden @3xl:inline-flex">
            <ExportSingleConversationButton leadId={id} leadName={name || contactIdentifier} />
          </span>
          <span className="hidden @3xl:inline-flex">{deleteButton}</span>
          <div className={cn('flex items-center gap-2 rounded-xl border px-2 py-1 cursor-pointer', isActive ? 'border-border' : 'border-orange-300 bg-orange-50')}>
            {agentSwitch}
            <span className={cn('text-sm font-medium', isActive ? 'text-text-primary' : 'text-orange-700')}>
              AI
            </span>
            {manualReplyMenu}
          </div>
        </div>}
      {manageModal}
    </header>;
};
export { ConversationHeader };